import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Snackbar,
} from '@mui/material';
import { ArrowBack as BackIcon, Print as PrintIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import saleService from '../../services/saleService';
import { Sale } from '../../types';
import ContratoVendaDocument from './ContratoVendaDocument';
import ContratoVendaPDFDoc from './ContratoVendaPDF';
import { loadTimbradoAsDataUrl } from './timbradoLoader';

const VendaView: React.FC = () => {
  const { saleId } = useParams<{ saleId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sale, setSale] = useState<Sale | null>(null);
  const [timbradoSrc, setTimbradoSrc] = useState<string | null>(null);
  const [timbradoError, setTimbradoError] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!saleId) return;
      try {
        setLoading(true);
        setError(null);
        const [data, timbrado] = await Promise.all([
          saleService.getSale(Number(saleId)),
          loadTimbradoAsDataUrl().catch((e) => {
            console.error(e);
            setTimbradoError('Não foi possível carregar o papel timbrado.');
            return null;
          }),
        ]);
        setSale(data);
        if (timbrado) setTimbradoSrc(timbrado);
        if (!data.vehicle) {
          setError('Dados do veículo desta venda não foram encontrados.');
        }
      } catch (e) {
        console.error(e);
        setError('Não foi possível carregar a venda.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [saleId]);

  const handlePrint = () => {
    if (!timbradoSrc) {
      setSnackbarMessage('Aguarde o timbrado carregar ou recarregue a página.');
      return;
    }
    window.print();
  };

  const handleDownloadPdf = async () => {
    if (!sale?.vehicle || !timbradoSrc) {
      setSnackbarMessage('Timbrado ainda não carregou. Tente novamente em instantes.');
      return;
    }
    setDownloadingPdf(true);
    try {
      const doc = (
        <ContratoVendaPDFDoc vehicle={sale.vehicle} sale={sale} timbradoSrc={timbradoSrc} />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrato-venda-${sale.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setSnackbarMessage('Erro ao gerar o PDF. Tente novamente.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !sale) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Venda não encontrada.'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/contratos')}>Voltar</Button>
      </Box>
    );
  }

  if (!sale.vehicle) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>Veículo desta venda não está disponível para o contrato.</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/contratos')}>Voltar</Button>
      </Box>
    );
  }

  return (
    <Box className="contrato-venda-view" sx={{ pb: 4 }}>
      <Box
        className="no-print"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Button startIcon={<BackIcon />} onClick={() => navigate('/contratos')} variant="outlined">
          Voltar
        </Button>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            startIcon={downloadingPdf ? <CircularProgress size={18} /> : <PdfIcon />}
            onClick={handleDownloadPdf}
            variant="contained"
            color="primary"
            disabled={downloadingPdf || !timbradoSrc}
          >
            {downloadingPdf ? 'Gerando...' : 'Baixar PDF'}
          </Button>
          <Button
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            variant="outlined"
            disabled={!timbradoSrc}
          >
            Imprimir
          </Button>
        </Box>
      </Box>

      {timbradoError && (
        <Alert severity="warning" sx={{ mb: 2 }} className="no-print">
          {timbradoError} O contrato será exibido sem timbrado.
        </Alert>
      )}

      {!timbradoSrc && !timbradoError && (
        <Alert severity="info" sx={{ mb: 2 }} className="no-print">
          Carregando papel timbrado…
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }} className="no-print">
        Na impressão, marque <strong>Gráficos de fundo</strong> (ou &quot;Background graphics&quot;) nas opções da
        impressora para o timbrado aparecer.
      </Alert>

      <Paper sx={{ p: 0, overflow: 'visible', bgcolor: 'transparent', boxShadow: { xs: 1, print: 'none' } }}>
        {timbradoSrc ? (
          <ContratoVendaDocument vehicle={sale.vehicle} sale={sale} timbradoSrc={timbradoSrc} />
        ) : (
          <Box sx={{ p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Paper>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={5000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default VendaView;
